import { useEffect } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Button } from 'components/Button'
import { SUPPORT_EMAIL } from 'utils/constants'
import { reportError } from 'utils/error'
import { AiErrorModalProps } from './Types'
import Styles from './AiErrorModal.module.scss'
import FearNot from './FearNot'
import { WebsiteScrapingError } from 'api/clients/BrandVoiceDetectionClient'
import UhOh from './UhOh'
import cx from 'classnames'
import { Importer } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/ContentSourcePicker'

const AiErrorModal = ({ onCancel, onTryAgain, error, isBrandVoice = false }: AiErrorModalProps) => {
  const noRetry = error instanceof Error && 'shouldRetry' in error && !error.shouldRetry
  const isWebsiteScrapingError = error instanceof WebsiteScrapingError || isBrandVoice

  useEffect(() => {
    if (error != null)
      reportError(error, 'metadata' in error && error.metadata != null ? { metaData: error.metadata } : undefined)
  }, [error])

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const buttonText = isWebsiteScrapingError ? 'Paste site text' : noRetry ? 'Back' : 'Try Again'

  return (
    <Modal.Root size="sm">
      <Modal.Header
        className={cx(Styles.header, { [Styles.websiteScrapingError]: isWebsiteScrapingError })}
        title={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          isWebsiteScrapingError
            ? 'Please try pasting the site text'
            : noRetry
            ? 'Please try a new URL'
            : 'Please try again'
        }
        size="lg"
      />

      <div className={Styles.content}>
        <div>
          {isWebsiteScrapingError ? <FearNot /> : <UhOh />}
          <ErrorMessage error={error} isBrandVoice={isBrandVoice} />
        </div>
      </div>

      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button onPress={onCancel} variant="tertiary" size="lg">
            Cancel
          </Button>
        </Modal.FooterContent>

        <Modal.FooterContent slot="right">
          <Button onPress={() => onTryAgain(isWebsiteScrapingError ? Importer.AddText : undefined)} size="lg">
            {buttonText}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

function ErrorMessage({ error, isBrandVoice }: { error?: Error; isBrandVoice?: boolean }) {
  if (error instanceof WebsiteScrapingError || isBrandVoice) return <PasteTextMessage error={error} />
  if (error == null || !('errorCode' in error)) return <DefaultErrorMessage />

  switch (error.errorCode) {
    case 'gibberish':
    case 'emptyPage':
    case 'other':
    case 'serverError':
      return (
        <p>
          Blaze is unable to generate a voice from the provided URL.
          <br />
          Please try adding the site text into the "Add Text" option, or using a different URL.
        </p>
      )
    case 'blockedMessage':
    case 'robotCheck':
      return (
        <p>
          The provided site is blocking Blaze from scanning its contents.
          <br />
          Please try either copy and pasting the site text into the "Add Text" option, or using a different URL.
        </p>
      )
    case 'loginPage':
    case 'paymentWall':
      return (
        <p>
          The provided site is blocking Blaze from scanning its contents due to a payment or login wall.
          <br />
          Please try either copy and pasting the site text into the "Add Text" option, or using a different URL.
        </p>
      )
    case 'campaignAborted':
      return (
        <p>
          A required step was aborted.
          <br />
          Please try again or contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if you are facing issues.
        </p>
      )
    case 'campaign':
      return (
        <p>
          There was a problem generating the campaign.
          <br />
          Our team has been notified.
          <br />
          Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if the issue persists.
        </p>
      )
    default:
      return <DefaultErrorMessage />
  }
}

function DefaultErrorMessage() {
  return (
    <p>
      Blaze encountered an error.
      <br />
      Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if the issue persists.
    </p>
  )
}

function PasteTextMessage({ error }: { error?: Error }) {
  const isCampaign = error && 'errorCode' in error && error.errorCode === 'campaign'
  return (
    <div className={Styles.pasteTextError}>
      <h2>Copy and paste text from the site to {isCampaign ? 'generate your campaign' : 'create your brand voice'}.</h2>
      <span>Some sites do not let us auto-analyze their content directly from URL.</span>
    </div>
  )
}

export { AiErrorModal }
